// src/com/auto_plan_bind/AutoPlanBindInput.js
import React, { useEffect, useMemo, useState } from 'react';
// import { Box } from '@mui/system';
// import { styled } from '@mui/material/styles';
import {
    Button, DialogContent, DialogActions, Grid, CircularProgress
} from '@mui/material';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmIntField from '../../common/VzmIntField';
import { ErrorHintBox, VzmPrint } from '../../common/VzmStyled';
import { useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { AutoPlanBindDictionary } from './AutoPlanBindDict';
import { arriveInfoListToSelectStoreRepo } from '../../value/AppModel';
import AutoTextField from '../../common/AutoTextField';

const InputMap = AutoPlanBindDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

/**
 * (AUTO_PLAN_BIND) 정보 생성 또는 편집 Dialog
 * 점포별 동적 정보
 * 메뉴: 자동배차 > 상온자동배차 >> [[동적정보]]
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2026-06-24 19:57:30 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function AutoPlanBindInput({
    open,
    baseData, // {dcCd, planDate, storeId, storeName}
    bindIndex, // 1 or 2
    item, // item to edit, if null then add new.
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const storeList = useReactiveVar(arriveInfoListToSelectStoreRepo);
    const targetStoreName = useMemo(()=>{
        const targetStore = storeList.records.find(store => store.storeId === input.data.targetStoreId);
        return targetStore ? targetStore.storeName : '없음';
    }, [input.data.targetStoreId, storeList]);
    const isEdit = Boolean(item);
    // const codeTree = useReactiveVar(codeTreeRepo);
    const openConfirmDialog = useConfirmDialog();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving || {loading:false, error:null, data:null}; // 전송 결과에 대한 View 기능 소화.

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            if(item) {
                inputSet.data = {...item};
            }
            else {
                const {dcCd, planDate, storeId} = baseData;
                inputSet.data = {...defaultInputData};
                inputSet.data.dcCd = dcCd;
                inputSet.data.planDate = planDate;
                inputSet.data.bindIndex = bindIndex;
                inputSet.data.targetStoreId = storeId;
            }
            setInput(inputSet);
        }
    }, [open, item, baseData, bindIndex]);

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='sm'
            onClose={onClickCancel}
            title={`동적배차 (${baseData.planDate})`}
        >
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <VzmPrint label={InputMap.bindIndex.label}
                            value={bindIndex} />
                    </Grid>
                    <Grid item xs={12}>
                        <VzmPrint label={InputMap.targetStoreId.label}
                            value={`[${input.data.targetStoreId}] ${targetStoreName}`} />
                    </Grid>
                    <Grid item xs={12} style={{paddingBottom: 32}}>
                        <VzmIntField
                            dict={InputMap.targetHocha}
                            value={input.data.targetHocha}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AutoTextField
                            dict={InputMap.bindStoreId}
                            value={input.data.bindStoreId || ''}
                            list={storeList.records.filter(store => store.storeId !== input.data.targetStoreId)}
                            disabled={loadingSave}
                            style={{minWidth:300, marginLeft: 10}}
                            onChange={onChangeValue}
                            usePortal={true}
                        />
                    </Grid>
                    <Grid item xs={12} style={{paddingBottom: 40}}>
                        <VzmIntField
                            dict={InputMap.bindHocha}
                            value={input.data.bindHocha}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                
                </Grid>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
